import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { activeTenant, api, get, refreshSession, setAccessToken, setActiveTenant } from "./api.ts";
import type { Account, CollectionDef, PageDef, Tenant, WorkflowDef } from "./types.ts";

interface Session {
  account: Account;
  tenants: Tenant[];
  tenant: Tenant | null;
  /** Owner or builder in the active store: may change structure. */
  canBuild: boolean;
  /** Has a built-in role (sees the structure's history). Custom roles only reach data. */
  isStaff: boolean;
  /** Owner: manages members. */
  isOwner: boolean;
  collections: CollectionDef[];
  pages: PageDef[];
  /** Workflows this person sees: all for built-in roles, the manual ones they may run otherwise. */
  workflows: WorkflowDef[];
  pending: number;
  switchTenant(id: string): void;
  reloadTenants(): Promise<void>;
  /** Re-reads structure (collections, pages, pending changesets) after a change. */
  reloadStructure(): Promise<void>;
  logout(): Promise<void>;
  toast(msg: string): void;
}

const Ctx = createContext<Session | null>(null);
const BUILTIN = ["dono", "construtor", "operador"];

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error("sem sessão");
  return s;
}

type Boot = { state: "loading" } | { state: "out" } | { state: "in"; account: Account; tenants: Tenant[] };

export function SessionProvider({ children, signedOut }: { children: ReactNode; signedOut: (onIn: (token: string) => void) => ReactNode }) {
  const [boot, setBoot] = useState<Boot>({ state: "loading" });
  const [tenantId, setTenantId] = useState<string | null>(activeTenant());
  const [collections, setCollections] = useState<CollectionDef[]>([]);
  const [pages, setPages] = useState<PageDef[]>([]);
  const [workflows, setWorkflows] = useState<WorkflowDef[]>([]);
  const [pending, setPending] = useState(0);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  const loadMe = useCallback(async () => {
    const me = await get<{ account: Account; tenants: Tenant[] }>("/v1/me", { tenant: false });
    setBoot({ state: "in", account: me.account, tenants: me.tenants });
    const keep = me.tenants.find((t) => t.id === activeTenant()) ?? me.tenants[0] ?? null;
    setActiveTenant(keep?.id ?? null);
    setTenantId(keep?.id ?? null);
  }, []);

  useEffect(() => {
    refreshSession().then((ok) => (ok ? loadMe().catch(() => setBoot({ state: "out" })) : setBoot({ state: "out" })));
  }, [loadMe]);

  const staff = boot.state === "in" && !!boot.tenants.find((t) => t.id === tenantId)?.roles.some((r) => BUILTIN.includes(r));
  const reloadStructure = useCallback(async () => {
    if (!tenantId) return;
    const [c, p, cs, w] = await Promise.all([
      get<{ collections: CollectionDef[] }>("/v1/collections"),
      get<{ pages: PageDef[] }>("/v1/pages"),
      staff ? get<{ changesets: unknown[] }>("/v1/changesets?status=pending&limit=200") : Promise.resolve({ changesets: [] }),
      get<{ workflows: WorkflowDef[] }>("/v1/workflows").catch(() => ({ workflows: [] })),
    ]);
    setCollections(c.collections);
    setPages(p.pages);
    setWorkflows(w.workflows);
    setPending(cs.changesets.length);
  }, [tenantId, staff]);

  useEffect(() => {
    if (boot.state === "in" && tenantId) reloadStructure().catch(() => undefined);
  }, [boot.state, tenantId, reloadStructure]);

  const value = useMemo<Session | null>(() => {
    if (boot.state !== "in") return null;
    const tenant = boot.tenants.find((t) => t.id === tenantId) ?? null;
    return {
      account: boot.account,
      tenants: boot.tenants,
      tenant,
      canBuild: !!tenant?.roles.some((r) => r === "dono" || r === "construtor"),
      isStaff: !!tenant?.roles.some((r) => BUILTIN.includes(r)),
      isOwner: !!tenant?.roles.includes("dono"),
      collections,
      pages,
      workflows,
      pending,
      switchTenant(id) {
        setActiveTenant(id);
        setTenantId(id);
        setCollections([]);
        setPages([]);
        setWorkflows([]);
      },
      reloadTenants: loadMe,
      reloadStructure,
      async logout() {
        await api("POST", "/auth/logout", undefined, { tenant: false }).catch(() => undefined);
        setAccessToken(null);
        setBoot({ state: "out" });
      },
      toast(msg) {
        setToastMsg(msg);
        setTimeout(() => setToastMsg((m) => (m === msg ? null : m)), 2800);
      },
    };
  }, [boot, tenantId, collections, pages, workflows, pending, loadMe, reloadStructure]);

  if (boot.state === "loading") return <div className="au muted">Carregando…</div>;
  if (boot.state === "out" || !value) {
    return signedOut((token) => {
      setAccessToken(token);
      loadMe().catch(() => setBoot({ state: "out" }));
    });
  }
  return (
    <Ctx.Provider value={value}>
      {children}
      {toastMsg && (
        <div className="toast" role="status">
          {toastMsg}
        </div>
      )}
    </Ctx.Provider>
  );
}

/** Loads a GET path; `reload` fetches again. Errors are kept for display. */
export function useGet<T>(path: string | null, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!path) return;
    let live = true;
    setError(null);
    get<T>(path)
      .then((d) => live && setData(d))
      .catch((e: Error) => live && setError(e));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, n, ...deps]);
  return { data, error, reload: () => setN((x) => x + 1) };
}
